import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Machine } from '../services/BackendDataService';
import { 
  getLineKPIConfig, 
  saveLineKPIConfig,
  type LineKPIConfiguration, 
  type MachineKPIConfig 
} from '../services/LineConfigService';
import { applyFlowLayout } from '../services/FlowLayoutService';
import { useUnifiedAuth } from '../auth/useUnifiedAuth';
import { getMachineTypeCategory } from '../utils/sapMetadataTranslations';
import { cx } from '../styles';
import { useThemeClasses } from '../providers/ThemeProvider';

interface LineConfigPanelProps {
  lineId: string;
  lineName: string;
  machines: Machine[];
  isOpen: boolean;
  onClose: () => void;
}

interface MachineConfigRow {
  machineId: string;
  machineName: string;
  machineType: string;
  unitName: string;
  unitsPerContainer: string;
  flowOrder: number;
  isLast: boolean;
}

const LineConfigPanel: React.FC<LineConfigPanelProps> = ({
  lineId,
  lineName,
  machines,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { token } = useUnifiedAuth();
  const themeClasses = useThemeClasses();
  
  const [machineConfigs, setMachineConfigs] = useState<MachineConfigRow[]>([]);
  const [outputUnitDescription, setOutputUnitDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Optional line-level targets
  const [efficiencyTarget, setEfficiencyTarget] = useState('');
  const [outputPerShiftTarget, setOutputPerShiftTarget] = useState('');
  const [rejectRatioTarget, setRejectRatioTarget] = useState('');
  
  // Sort machines by flow order and filter only machines with data
  const sortedMachines = useMemo(() => {
    const withData = machines.filter(m => m.datenVerfuegbar);
    return applyFlowLayout(lineId, withData);
  }, [lineId, machines]);
  
  // Initialize config from backend
  useEffect(() => {
    if (!isOpen || !token) return;
    
    const loadConfig = async () => {
      const savedConfig = await getLineKPIConfig(lineId, token);
      
      const configRows: MachineConfigRow[] = sortedMachines.map((machine, index) => {
        const machineId = machine.maschinennummer || machine.maschineBK;
        const savedMachineConfig = savedConfig?.machines.find(m => m.machineId === machineId);
        
        // Build display name
        let rawType = machine.bautyp || machine.machineType || '';
        const machineNum = machine.maschinennummer || machine.maschineBK || '';
        if (rawType && machineNum && rawType.startsWith(machineNum)) {
          rawType = rawType.replace(new RegExp(`^${machineNum}\\s*[-–]?\\s*`), '').trim();
        }
        const modelName = rawType.replace(/^Modell\s+/i, '');
        const typeCategory = getMachineTypeCategory(machine.bautyp || machine.machineType || '');
        
        return {
          machineId,
          machineName: modelName || machineNum,
          machineType: typeCategory !== 'unknown' ? typeCategory : rawType,
          unitName: savedMachineConfig?.unitConversion?.unitName || '',
          unitsPerContainer: savedMachineConfig?.unitConversion?.unitsPerContainer?.toString() || '',
          flowOrder: machine.flowOrder ?? 0,
          isLast: index === sortedMachines.length - 1,
        };
      });
      
      setMachineConfigs(configRows);
      setOutputUnitDescription(savedConfig?.outputUnitDescription || '');
      setEfficiencyTarget(savedConfig?.targets?.efficiency?.toString() || '');
      setOutputPerShiftTarget(savedConfig?.targets?.outputPerShift?.toString() || '');
      setRejectRatioTarget(savedConfig?.targets?.rejectRatio?.toString() || '');
    };
    
    loadConfig();
  }, [isOpen, lineId, sortedMachines, token]);
  
  const handleUnitNameChange = (machineId: string, value: string) => {
    setMachineConfigs(prev => prev.map(c => 
      c.machineId === machineId ? { ...c, unitName: value } : c
    ));
  };
  
  const handleUnitsPerContainerChange = (machineId: string, value: string) => {
    setMachineConfigs(prev => prev.map(c => 
      c.machineId === machineId ? { ...c, unitsPerContainer: value } : c
    ));
  };
  
  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    
    try {
      // Build machine configs (last machine in flow is automatically output reference)
      const machinesConfig: MachineKPIConfig[] = machineConfigs.map(c => {
        const config: MachineKPIConfig = {
          machineId: c.machineId,
        };
        
        if (c.unitName && c.unitsPerContainer) {
          const unitsNum = parseFloat(c.unitsPerContainer);
          if (!isNaN(unitsNum) && unitsNum > 0) {
            config.unitConversion = {
              unitName: c.unitName,
              unitsPerContainer: unitsNum,
            };
          }
        }
        
        return config;
      });
      
      const targets: LineKPIConfiguration['targets'] = {};
      if (efficiencyTarget) {
        const val = parseFloat(efficiencyTarget);
        if (!isNaN(val) && val >= 0 && val <= 100) {
          targets.efficiency = val;
        }
      }
      if (outputPerShiftTarget) {
        const val = parseFloat(outputPerShiftTarget);
        if (!isNaN(val) && val > 0) {
          targets.outputPerShift = val;
        }
      }
      if (rejectRatioTarget) {
        const val = parseFloat(rejectRatioTarget);
        if (!isNaN(val) && val >= 0 && val <= 100) {
          targets.rejectRatio = val;
        }
      }
      
      const configToSave = {
        machines: machinesConfig,
        targets: Object.keys(targets).length > 0 ? targets : undefined,
        outputUnitDescription: outputUnitDescription || undefined,
      };
      
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const success = await saveLineKPIConfig(lineId, configToSave, token);
      
      if (!success) {
        throw new Error('Failed to save');
      }
      
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to save line config:', error);
      alert(t('saveError') || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  // Check if any unit conversions are configured
  const hasUnitConversions = machineConfigs.some(c => c.unitName && c.unitsPerContainer);
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className={cx(
        'w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-xl shadow-2xl',
        themeClasses.bgSurface
      )}>
        {/* Header */}
        <div className={cx(
          'flex items-center justify-between px-6 py-4 border-b',
          themeClasses.borderPrimary
        )}>
          <div>
            <h2 className={cx('text-xl font-bold', themeClasses.textPrimary)}>
              {t('lineKPIConfiguration') || 'Line KPI Configuration'}
            </h2>
            <p className={cx('text-sm mt-1', themeClasses.textSecondary)}>
              {lineName}
            </p>
          </div>
          <button
            onClick={onClose}
            className={cx(
              'p-2 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-colors',
              themeClasses.textSecondary
            )}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6 space-y-6">
          {/* Unit Conversions */}
          <div>
            <h3 className={cx('text-lg font-semibold mb-3', themeClasses.textPrimary)}>
              {t('packagingStages') || 'Packaging Stages'} <span className={themeClasses.textMuted}>({t('optional') || 'optional'})</span>
            </h3>
            
            <div className={cx('border rounded-lg overflow-hidden', themeClasses.borderPrimary)}>
              <table className="w-full">
                <thead className={cx('bg-gray-50 dark:bg-gray-800')}>
                  <tr>
                    <th className={cx('px-4 py-3 text-left text-sm font-medium', themeClasses.textSecondary)}>
                      {t('machine') || 'Machine'}
                    </th>
                    <th className={cx('px-4 py-3 text-left text-sm font-medium', themeClasses.textSecondary)}>
                      {t('produces') || 'Produces'}
                    </th>
                    <th className={cx('px-4 py-3 text-left text-sm font-medium', themeClasses.textSecondary)}>
                      {t('containsItems') || 'Contains (items)'}
                    </th>
                  </tr>
                </thead>
                <tbody className={cx('divide-y', themeClasses.borderPrimary)}>
                  {machineConfigs.map((config, index) => {
                    // Dynamic placeholders based on position
                    const placeholders = ['packets', 'cartridges', 'boxes', 'pallets'];
                    const placeholder = placeholders[index] || 'units';
                    const isFirst = index === 0;
                    
                    return (
                      <tr 
                        key={config.machineId}
                        className={cx(
                          config.isLast ? 'bg-green-50 dark:bg-green-900/10' : '',
                          'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                        )}
                      >
                        <td className={cx('px-4 py-3', themeClasses.textPrimary)}>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono text-gray-400">{index + 1}.</span>
                            <span className="font-medium">{config.machineName}</span>
                            {config.isLast && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-300">
                                {t('output') || 'output'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={config.unitName}
                            onChange={(e) => handleUnitNameChange(config.machineId, e.target.value)}
                            placeholder={placeholder}
                            className={cx(
                              'w-full px-3 py-1.5 text-sm rounded border',
                              themeClasses.bgSurface,
                              themeClasses.borderPrimary,
                              themeClasses.textPrimary,
                              'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                            )}
                          />
                        </td>
                        <td className="px-4 py-3">
                          {isFirst ? (
                            <span className={cx('text-sm italic', themeClasses.textMuted)}>
                              {t('baseUnit') || '— base unit —'}
                            </span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={config.unitsPerContainer}
                                onChange={(e) => handleUnitsPerContainerChange(config.machineId, e.target.value)}
                                placeholder="10"
                                className={cx(
                                  'w-20 px-3 py-1.5 text-sm rounded border',
                                  themeClasses.bgSurface,
                                  themeClasses.borderPrimary,
                                  themeClasses.textPrimary,
                                  'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                                )}
                              />
                              {index > 0 && machineConfigs[index - 1]?.unitName && (
                                <span className={cx('text-sm', themeClasses.textMuted)}>
                                  {machineConfigs[index - 1].unitName}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className={cx('text-sm mt-2', themeClasses.textMuted)}>
              {t('packagingHelp') || 'Leave empty if all machines count the same unit.'}
            </p>
          </div>
          
          {/* Output Unit Description */}
          {hasUnitConversions && (
            <div>
              <label className={cx('block text-sm font-medium mb-2', themeClasses.textPrimary)}>
                {t('outputUnitDescription') || 'Final Output Description'}
              </label>
              <input
                type="text"
                value={outputUnitDescription}
                onChange={(e) => setOutputUnitDescription(e.target.value)}
                placeholder={t('egBoxesOf20Cartridges') || 'e.g., boxes of 20 cartridges (200 packets)'}
                className={cx(
                  'w-full px-4 py-2 rounded-lg border',
                  themeClasses.bgSurface,
                  themeClasses.borderPrimary,
                  themeClasses.textPrimary,
                  'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                )}
              />
            </div>
          )}
          
          {/* Line Targets (Optional) */}
          <div>
            <h3 className={cx('text-lg font-semibold mb-3', themeClasses.textPrimary)}>
              {t('lineTargets') || 'Line Targets'} <span className={themeClasses.textMuted}>({t('optional') || 'optional'})</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={cx('block text-sm font-medium mb-2', themeClasses.textSecondary)}>
                  {t('efficiencyTarget') || 'Efficiency Target'} (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={efficiencyTarget}
                  onChange={(e) => setEfficiencyTarget(e.target.value)}
                  placeholder="85"
                  className={cx(
                    'w-full px-4 py-2 rounded-lg border',
                    themeClasses.bgSurface,
                    themeClasses.borderPrimary,
                    themeClasses.textPrimary,
                    'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  )}
                />
              </div>
              <div>
                <label className={cx('block text-sm font-medium mb-2', themeClasses.textSecondary)}>
                  {t('outputPerShiftTarget') || 'Output/Shift Target'}
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={outputPerShiftTarget}
                  onChange={(e) => setOutputPerShiftTarget(e.target.value)}
                  placeholder="1000"
                  className={cx(
                    'w-full px-4 py-2 rounded-lg border',
                    themeClasses.bgSurface,
                    themeClasses.borderPrimary,
                    themeClasses.textPrimary,
                    'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  )}
                />
              </div>
              <div>
                <label className={cx('block text-sm font-medium mb-2', themeClasses.textSecondary)}>
                  {t('rejectRatioTarget') || 'Reject Ratio Target'} (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={rejectRatioTarget}
                  onChange={(e) => setRejectRatioTarget(e.target.value)}
                  placeholder="2"
                  className={cx(
                    'w-full px-4 py-2 rounded-lg border',
                    themeClasses.bgSurface,
                    themeClasses.borderPrimary,
                    themeClasses.textPrimary,
                    'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  )}
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className={cx(
          'flex items-center justify-end gap-3 px-6 py-4 border-t',
          themeClasses.borderPrimary
        )}>
          {saveSuccess && (
            <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {t('saved') || 'Saved!'}
            </span>
          )}
          <button
            onClick={onClose}
            className={cx(
              'px-4 py-2 rounded-lg font-medium transition-colors',
              'text-gray-700 dark:text-gray-300',
              'hover:bg-gray-100 dark:hover:bg-gray-700'
            )}
          >
            {t('cancel') || 'Cancel'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={cx(
              'px-6 py-2 rounded-lg font-medium transition-colors',
              'bg-blue-600 text-white hover:bg-blue-700',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'flex items-center gap-2'
            )}
          >
            {saving && (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            {t('save') || 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LineConfigPanel;
